// "How long is this file the user just picked?" — for a File/Blob still on the device,
// before anything has been uploaded.
//
// The browser's own <video> element answers this for the formats it can decode (mp4,
// webm, mov, and mkv where the codecs happen to be supported). It cannot decode FLV or
// AVI at all: `loadedmetadata` never fires, `onerror` does, and the duration comes back
// as 0 — which is why those cards showed no duration while uploading or afterwards, since
// the value sent at upload time is what the library row is later rendered from.
//
// Both formats carry their duration in a header near the start of the file, so a small
// slice is enough to read it without touching the rest of a multi-GB upload.

// Chosen to comfortably cover an FLV onMetaData tag and an AVI hdrl LIST, both of which
// sit at the very start of their files.
const HEADER_BYTES = 256 * 1024;

const readHead = async (file) => {
  const slice = file.slice(0, Math.min(HEADER_BYTES, file.size));
  const buffer = await slice.arrayBuffer();
  return new DataView(buffer);
};

const fourCC = (view, offset) => {
  if (offset + 4 > view.byteLength) return '';
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3)
  );
};

/**
 * FLV: the duration lives in the onMetaData SCRIPTDATA tag, as an AMF0 ECMA-array
 * property. Rather than walking the tag structure, find the "duration" key and read the
 * value that follows it: AMF0 encodes an object property as
 *   UI16 name length | name bytes | type marker | value
 * so a number property is the 8 ASCII bytes "duration", then marker 0x00, then a
 * big-endian float64 of seconds.
 */
const parseFlvDurationSec = (view) => {
  if (fourCC(view, 0).slice(0, 3) !== 'FLV') return 0;

  const KEY = 'duration';
  const limit = view.byteLength - (KEY.length + 9);
  for (let i = 0; i < limit; i++) {
    let matched = true;
    for (let k = 0; k < KEY.length; k++) {
      if (view.getUint8(i + k) !== KEY.charCodeAt(k)) {
        matched = false;
        break;
      }
    }
    if (!matched) continue;

    // 0x00 is AMF0's number marker. Anything else means we matched the word somewhere
    // that isn't a numeric property (a string value, say) — keep looking.
    if (view.getUint8(i + KEY.length) !== 0x00) continue;

    const seconds = view.getFloat64(i + KEY.length + 1, false);
    if (isFinite(seconds) && seconds > 0) return seconds;
  }
  return 0;
};

/**
 * AVI (RIFF): the main header chunk `avih` holds microseconds-per-frame and a total frame
 * count; their product is the duration. Some encoders leave dwTotalFrames at 0, in which
 * case the video stream's own `strh` header answers it instead, via dwLength scaled by
 * dwRate. All RIFF integers are little-endian.
 */
const parseAviDurationSec = (view) => {
  if (fourCC(view, 0) !== 'RIFF' || fourCC(view, 8) !== 'AVI ') return 0;

  let microSecPerFrame = 0;
  let totalFrames = 0;

  for (let i = 12; i + 8 <= view.byteLength; i++) {
    if (fourCC(view, i) !== 'avih') continue;
    const data = i + 8; // skip the fourcc and the chunk size
    if (data + 20 > view.byteLength) break;
    microSecPerFrame = view.getUint32(data, true);
    totalFrames = view.getUint32(data + 16, true);
    break;
  }

  if (microSecPerFrame > 0 && totalFrames > 0) {
    return (microSecPerFrame * totalFrames) / 1e6;
  }

  // Fall back to the first stream header that describes a video stream ('vids').
  for (let i = 12; i + 8 <= view.byteLength; i++) {
    if (fourCC(view, i) !== 'strh') continue;
    const data = i + 8;
    if (data + 36 > view.byteLength) continue;
    if (fourCC(view, data) !== 'vids') continue;

    const scale = view.getUint32(data + 20, true);
    const rate = view.getUint32(data + 24, true);
    const length = view.getUint32(data + 32, true);
    if (rate > 0 && length > 0) return (length * scale) / rate;
  }

  return 0;
};

// The <video> element, for everything the browser can decode itself.
const getNativeDurationSec = (file) =>
  new Promise((resolve) => {
    let objectUrl = null;
    const cleanup = () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const { duration } = video;
        cleanup();
        resolve(duration && isFinite(duration) ? duration : 0);
      };
      video.onerror = () => {
        cleanup();
        resolve(0);
      };
      objectUrl = URL.createObjectURL(file);
      video.src = objectUrl;
    } catch (e) {
      cleanup();
      resolve(0);
    }
  });

const extensionOf = (nameOrUrl) =>
  String(nameOrUrl || '')
    .toLowerCase()
    .split(/[?#]/)[0]
    .split('.')
    .pop();

const parseHeaderDurationSec = (view, ext) => {
  const seconds = ext === 'avi' ? parseAviDurationSec(view) : parseFlvDurationSec(view);
  if (seconds > 0) return seconds;
  // An extension can lie - try the other parser before giving up.
  return ext === 'avi' ? parseFlvDurationSec(view) : parseAviDurationSec(view);
};

// Same as readHead, but for a file that already lives on S3. Deliberately bounded: a
// Range request that the server chooses not to honour answers 200 with the WHOLE file,
// and these can be gigabytes - so stop reading (and abort the request) either at the
// header size or the moment the response comes back as anything but 206.
const fetchHead = async (url) => {
  const controller = new AbortController();
  try {
    const response = await fetch(url, {
      headers: { Range: `bytes=0-${HEADER_BYTES - 1}` },
      signal: controller.signal,
    });
    if (!response.ok || response.status !== 206 || !response.body) {
      controller.abort();
      return null;
    }

    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;
    while (received < HEADER_BYTES) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
    }
    controller.abort();

    const merged = new Uint8Array(received);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    return new DataView(merged.buffer);
  } catch (e) {
    controller.abort();
    return null;
  }
};

const getNativeDurationSecFromUrl = (url) =>
  new Promise((resolve) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const { duration } = video;
        resolve(duration && isFinite(duration) ? duration : 0);
      };
      video.onerror = () => resolve(0);
      video.src = url;
    } catch (e) {
      resolve(0);
    }
  });

/**
 * Duration in seconds of an already-uploaded video at `url`, or 0 when it can't be
 * determined. The URL counterpart of getLocalVideoDurationSec, for cards whose row came
 * back from the server without a duration on it. Never rejects.
 */
export const getRemoteVideoDurationSec = async (url) => {
  if (!url) return 0;

  const ext = extensionOf(url);
  const needsHeaderParse = ext === 'flv' || ext === 'avi';

  if (!needsHeaderParse) {
    return getNativeDurationSecFromUrl(url);
  }

  const view = await fetchHead(url);
  if (view) {
    const seconds = parseHeaderDurationSec(view, ext);
    if (seconds > 0) return seconds;
  }
  return 0;
};

/**
 * Duration in seconds of a local video File, or 0 when it can't be determined.
 * Never rejects — callers treat 0 as "unknown" and simply show no duration.
 */
export const getLocalVideoDurationSec = async (file) => {
  if (!file) return 0;

  const ext = extensionOf(file?.name);
  const needsHeaderParse = ext === 'flv' || ext === 'avi';

  if (!needsHeaderParse) {
    const nativeDuration = await getNativeDurationSec(file);
    if (nativeDuration > 0) return nativeDuration;
  }

  try {
    const view = await readHead(file);
    const seconds = parseHeaderDurationSec(view, ext);
    if (seconds > 0) return seconds;
  } catch (e) {
    console.warn('[localVideoDuration] could not read the file header', e);
  }

  // Last resort for a header-parsed format: the browser might surprise us.
  return needsHeaderParse ? getNativeDurationSec(file) : 0;
};

// ---------------------------------------------------------------------------
// YouTube
// ---------------------------------------------------------------------------
// A YouTube resource is a link, so there is no file to measure and no <video> that can
// load it. The duration is readable without an API key by briefly instantiating YouTube's
// own IFrame player off-screen and asking it: getDuration() is populated by the time the
// player reports ready. (The Data API v3 would also answer this, but it needs a key this
// app doesn't carry.)

export const extractYoutubeId = (url) => {
  if (typeof url !== 'string') return null;
  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/v\/([A-Za-z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

let ytApiPromise = null;

const loadYouTubeIframeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve, reject) => {
    // The API calls exactly one global hook when it finishes loading, so chain onto
    // whatever was already there rather than replacing it.
    const previousHook = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousHook === 'function') previousHook();
      resolve(window.YT);
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => {
      ytApiPromise = null;
      reject(new Error('Failed to load the YouTube IFrame API'));
    };
    document.head.appendChild(script);
  });

  return ytApiPromise;
};

// Durations never change, and a list can hold several cards for the same video - so only
// ever stand up one player per id.
const youtubeDurationCache = new Map();

const READY_TIMEOUT_MS = 8000;

/**
 * Duration in seconds of a YouTube video, or 0 when it can't be determined - an invalid
 * or private link, a live stream (which has no fixed length), or no route to youtube.com.
 * Never rejects.
 */
export const getYoutubeDurationSec = async (url) => {
  const videoId = extractYoutubeId(url);
  if (!videoId) return 0;
  if (youtubeDurationCache.has(videoId)) return youtubeDurationCache.get(videoId);

  let seconds = 0;
  try {
    const YT = await loadYouTubeIframeApi();
    seconds = await new Promise((resolve) => {
      // YT.Player REPLACES the element it is given with an iframe, so mount it inside a
      // wrapper that stays ours to remove afterwards.
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;';
      const mount = document.createElement('div');
      wrapper.appendChild(mount);
      document.body.appendChild(wrapper);

      let settled = false;
      let player = null;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try { if (player && player.destroy) player.destroy(); } catch (e) { /* already gone */ }
        try { wrapper.remove(); } catch (e) { /* already gone */ }
        resolve(value);
      };
      // A player that never reports ready (blocked, removed video, no network) would
      // otherwise leave this pending forever.
      const timer = setTimeout(() => finish(0), READY_TIMEOUT_MS);

      try {
        player = new YT.Player(mount, {
          videoId,
          width: '1',
          height: '1',
          playerVars: { autoplay: 0, controls: 0, modestbranding: 1 },
          events: {
            onReady: (event) => {
              const value = event?.target?.getDuration?.();
              finish(isFinite(value) && value > 0 ? value : 0);
            },
            onError: () => finish(0),
          },
        });
      } catch (e) {
        finish(0);
      }
    });
  } catch (e) {
    console.warn('[localVideoDuration] could not read the YouTube duration', e);
    seconds = 0;
  }

  youtubeDurationCache.set(videoId, seconds);
  return seconds;
};

export default getLocalVideoDurationSec;
